import React, { useState, useEffect } from 'react';
import { getProfile, acceptDisclaimer } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const DisclaimerModal = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user needs to see disclaimer (only for paid plans)
    const checkDisclaimerStatus = async () => {
      if (!user) return;

      try {
        const response = await getProfile();
        const disclaimerAccepted = response.data.disclaimer_accepted;
        const tier = response.data.tier;

        // Only show for paid tiers (not free tier)
        const isPaidTier = ['monthly', 'pro', 'enterprise', 'pay_per_scan'].includes(tier);

        // Show modal if user is on paid plan AND hasn't accepted disclaimer
        if (isPaidTier && !disclaimerAccepted) {
          setShowModal(true);
        }
      } catch (error) {
        console.error('Failed to check disclaimer status:', error);
      }
    };

    checkDisclaimerStatus();
  }, [user]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      await acceptDisclaimer();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to accept disclaimer:', error);
      alert('Failed to accept disclaimer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Important Legal Disclaimer
            </h2>
            <p className="text-sm text-gray-600">
              Please read and accept before using ContractGuard
            </p>
          </div>

          {/* Disclaimer Content */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <span className="text-red-600 text-sm font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">This is NOT legal advice.</strong> ContractGuard is an AI-powered analysis tool designed to help identify potential issues in contracts. It does NOT replace professional legal counsel.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <span className="text-red-600 text-sm font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">Always consult a qualified solicitor or lawyer.</strong> Only a licensed legal professional can provide advice specific to your situation and jurisdiction.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <span className="text-red-600 text-sm font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">AI analysis has limitations.</strong> While our AI is trained on legal concepts, it may miss issues, make errors, or provide incomplete analysis. Never rely solely on AI analysis for important legal decisions.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <span className="text-red-600 text-sm font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">You use this service at your own risk.</strong> ContractGuard makes no warranties about the accuracy or completeness of the analysis. We shall not be liable for any damages arising from your reliance on this analysis.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                <span className="text-blue-600 text-sm font-bold">🔒</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">Your privacy matters.</strong> We do not cache or store your contract content beyond what's necessary to provide the analysis service. See our Privacy Policy for details.
                </p>
              </div>
            </div>
          </div>

          {/* Acceptance Section */}
          <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-primary-900">
              By clicking "I Understand and Accept" below, you acknowledge that:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-primary-800">
              <li>• You have read and understood this disclaimer</li>
              <li>• You will not rely on ContractGuard as a substitute for legal advice</li>
              <li>• You will consult a qualified solicitor for important legal matters</li>
              <li>• You accept the limitations and risks of AI-powered analysis</li>
            </ul>
          </div>

          {/* Accept Button */}
          <button
            onClick={handleAccept}
            disabled={loading}
            className="w-full bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed px-8 py-4 rounded-lg font-semibold text-lg shadow-lg transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'I Understand and Accept'
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            This disclaimer will only be shown once. You can review it anytime in downloaded reports.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
